import type { DiscardOrigin, DrawSource, GamePlayer, GameState } from "../types/protocol";
import {
  FLIGHT_DURATION,
  flightLanding,
  landingAfter,
  type ActiveCover,
  type CardCover,
  type CardFlight,
  type FlightAnchor,
  type FlightRole,
} from "./card-flight";

/** Colonnes verticales de la grille, dans l'ordre des index du plateau. */
const GRID_COLUMNS = [
  [0, 4, 8],
  [1, 5, 9],
  [2, 6, 10],
  [3, 7, 11],
] as const;

/** Le temps de lire les trois cartes alignées avant qu'elles ne s'envolent. */
const COLUMN_READING_BEAT = 0.34;

interface MoveBase {
  id: string;
  sequence: number;
  revision: number;
  playerId: string;
  playerName: string;
  /** Le joueur devant l'écran : ses repères et son rythme diffèrent. */
  isSelf: boolean;
}

export interface PickupEvent extends MoveBase {
  source: DrawSource;
  /** `null` tant que la carte reste secrète : une pioche vue d'en face. */
  publicValue: number | null;
}

export interface DiscardEvent extends MoveBase {
  origin: DiscardOrigin;
  position: number | null;
  value: number;
  startsHidden: boolean;
  /** Sommet à garder affiché tant que la carte n'a pas atterri. */
  previousDiscardTop: number | null;
}

/** Carte tenue qui rejoint la grille ; l'envers d'un dépôt venu d'une case. */
export interface PlacementEvent {
  position: number;
  /** `null` si la valeur posée reste indéterminable. */
  value: number | null;
}

export interface ColumnRemovalEvent {
  id: string;
  playerId: string;
  isSelf: boolean;
  positions: number[];
  value: number;
}

export interface FlightGroup {
  id: string;
  flights: CardFlight[];
  /** Ce que le groupe raconte, pour les libellés et les repères du plateau. */
  pickup: PickupEvent | null;
  discard: DiscardEvent | null;
}

export interface CardMotionPlan {
  pickup: PickupEvent | null;
  discard: DiscardEvent | null;
  /** Groupes joués l'un après l'autre ; les vols d'un même groupe sont simultanés. */
  groups: FlightGroup[];
}

export interface DeriveOptions {
  /** Autorise plusieurs instantanés réseau regroupés dans un rendu React. */
  allowRevisionGap?: boolean;
}

export const EMPTY_PLAN: CardMotionPlan = { pickup: null, discard: null, groups: [] };

const sameRound = (previous: GameState, current: GameState) =>
  previous.roomCode === current.roomCode && previous.round === current.round;

/**
 * Une reprise de session tombe au milieu d'un tour déjà joué : son instantané
 * ne doit rejouer aucun mouvement, seul un enchaînement continu le mérite.
 */
const isLive = (previous: GameState, current: GameState, options: DeriveOptions) =>
  current.revision === previous.revision + 1 ||
  (options.allowRevisionGap === true && current.revision > previous.revision + 1);

const identify = (
  current: GameState,
  playerId: string,
): Pick<MoveBase, "playerId" | "playerName" | "isSelf"> | null => {
  const player = current.players.find(({ id }) => id === playerId);
  if (!player) return null;
  return { playerId: player.id, playerName: player.name, isSelf: player.id === current.selfPlayerId };
};

/** Détecte une carte quittant une pile pour une main, la sienne ou une autre. */
export const derivePickup = (
  previous: GameState,
  current: GameState,
  options: DeriveOptions = {},
): PickupEvent | null => {
  const pickup = current.lastPickup;
  if (
    !pickup ||
    pickup.id === previous.lastPickup?.id ||
    !sameRound(previous, current) ||
    !isLive(previous, current, options)
  ) {
    return null;
  }

  const player = identify(current, pickup.playerId);
  if (!player) return null;

  return {
    ...player,
    id: pickup.id,
    sequence: pickup.sequence,
    revision: current.revision,
    source: pickup.source,
    // Une carte tirée face cachée n'est publique que pour celui qui la tient.
    publicValue: pickup.value ?? (player.isSelf ? current.heldCard : null),
  };
};

/** Détecte un dépôt vers la défausse, venu d'une main ou d'une case de grille. */
export const deriveDiscard = (
  previous: GameState,
  current: GameState,
  options: DeriveOptions = {},
): DiscardEvent | null => {
  const discard = current.lastDiscard;
  if (
    !discard ||
    discard.id === previous.lastDiscard?.id ||
    !sameRound(previous, current) ||
    !isLive(previous, current, options)
  ) {
    return null;
  }

  const player = identify(current, discard.playerId);
  if (!player) return null;

  return {
    ...player,
    id: discard.id,
    sequence: discard.sequence,
    revision: current.revision,
    origin: discard.origin,
    position: discard.position,
    value: discard.value,
    startsHidden: discard.startsHidden,
    previousDiscardTop: previous.discardTop,
  };
};

const gridOf = (state: GameState, playerId: string): GamePlayer["grid"] | null =>
  state.players.find(({ id }) => id === playerId)?.grid ?? null;

/**
 * Valeur de la carte qui vient d'être posée. Elle se lit dans la grille, sauf
 * quand elle a complété une colonne : la case est alors déjà vide, et ce sont
 * les deux autres cartes de la colonne qui disent ce qui s'est posé.
 */
const placedValue = (
  previous: GameState,
  current: GameState,
  playerId: string,
  position: number,
): number | null => {
  const landed = gridOf(current, playerId)?.[position];
  if (landed?.state === "revealed" && landed.value !== null) return landed.value;

  const before = gridOf(previous, playerId);
  const column = GRID_COLUMNS[position % GRID_COLUMNS.length] ?? [];
  for (const index of column) {
    const card = index === position ? undefined : before?.[index];
    if (card?.state === "revealed" && card.value !== null) return card.value;
  }
  return null;
};

/** Un dépôt venu d'une case implique une carte tenue qui a pris sa place. */
export const derivePlacement = (
  previous: GameState,
  current: GameState,
  discard: DiscardEvent | null,
): PlacementEvent | null => {
  if (!discard || discard.origin !== "grid" || discard.position === null) return null;
  return {
    position: discard.position,
    value: placedValue(previous, current, discard.playerId, discard.position),
  };
};

/**
 * Valeur d'une colonne qui vient d'être retirée. Elle se lit dans l'instantané
 * d'avant le coup, où l'une des trois cases porte encore ce que le coup y a
 * remplacé : la première valeur rencontrée est donc parfois celle qu'on vient
 * de jeter, et non celle de la colonne. Un coup ne touche qu'une case ; les
 * deux autres portaient déjà la valeur qui aligne la colonne. C'est cette
 * valeur partagée que l'on retient — sans quoi un échange en tête de colonne
 * fait s'envoler trois cartes marquées de la carte qu'elles remplacent.
 */
const columnValue = (before: GamePlayer["grid"], column: readonly number[]): number | null => {
  const values = column.reduce<number[]>((seen, index) => {
    const card = before[index];
    if (card?.state === "revealed" && card.value !== null) seen.push(card.value);
    return seen;
  }, []);
  // Aucune paire : l'instantané d'avant est trop ancien pour dire la colonne.
  // Mieux vaut renoncer au mouvement que faire voler une valeur inventée.
  return values.find((value) => values.filter((other) => other === value).length > 1) ?? null;
};

/** Colonnes de trois valeurs identiques parties à la défausse pendant ce coup. */
export const deriveColumnRemovals = (
  previous: GameState,
  current: GameState,
  options: DeriveOptions = {},
): ColumnRemovalEvent[] => {
  if (!sameRound(previous, current) || !isLive(previous, current, options)) return [];
  // Une fin de manche retourne et retire des colonnes chez tout le monde d'un
  // coup : ce n'est plus un geste à suivre, et le tableau des scores arrive.
  if (current.phase === "round-ended" || current.phase === "game-ended") return [];

  const removals: ColumnRemovalEvent[] = [];
  for (const player of current.players) {
    const before = gridOf(previous, player.id);
    if (!before) continue;

    for (const column of GRID_COLUMNS) {
      const removedNow = column.every((index) => player.grid[index]?.state === "removed");
      const removedBefore = column.every((index) => before[index]?.state === "removed");
      if (!removedNow || removedBefore) continue;

      const value = columnValue(before, column);
      if (value === null) continue;
      removals.push({
        id: `${current.revision}-${player.id}-${column.join("")}`,
        playerId: player.id,
        isSelf: player.id === current.selfPlayerId,
        positions: [...column],
        value,
      });
    }
  }
  return removals;
};

const roleOf = (event: { isSelf: boolean }): FlightRole => (event.isSelf ? "self" : "opponent");

const handAnchor = (event: { playerId: string; isSelf: boolean }): FlightAnchor => ({
  kind: "hand",
  playerId: event.playerId,
  isSelf: event.isSelf,
});

const cardAnchor = (
  event: { playerId: string; isSelf: boolean },
  index: number,
): FlightAnchor => ({ kind: "card", playerId: event.playerId, isSelf: event.isSelf, index });

const DISCARD_PILE: FlightAnchor = { kind: "pile", pile: "discard" };

export const landsOnDiscard = (flight: CardFlight): boolean =>
  flight.to.kind === "pile" && flight.to.pile === "discard";

const pickupGroup = (pickup: PickupEvent): FlightGroup => ({
  id: `pickup-${pickup.id}`,
  pickup,
  discard: null,
  flights: [
    {
      id: `pickup-${pickup.id}`,
      role: roleOf(pickup),
      tone: "pickup",
      playerId: pickup.playerId,
      from: { kind: "pile", pile: pickup.source },
      to: handAnchor(pickup),
      value: pickup.publicValue,
      // Une carte de pioche décolle dos visible et ne se retourne que pour
      // celui qui la découvre en la prenant.
      startsHidden: pickup.source === "deck",
      delay: 0,
      covers: [],
    },
  ],
});

const discardGroup = (discard: DiscardEvent, placement: PlacementEvent | null): FlightGroup => {
  const role = roleOf(discard);
  const fromGrid = discard.origin === "grid" && discard.position !== null;
  const covers: CardCover[] = fromGrid
    ? [
        {
          playerId: discard.playerId,
          index: discard.position as number,
          face: null,
          fadesAt: landingAfter(role),
        },
      ]
    : [];

  const flights: CardFlight[] = [
    {
      id: `discard-${discard.id}`,
      role,
      tone: "discard",
      playerId: discard.playerId,
      from: fromGrid ? cardAnchor(discard, discard.position as number) : handAnchor(discard),
      to: DISCARD_PILE,
      value: discard.value,
      startsHidden: discard.startsHidden,
      delay: 0,
      covers,
      discardTopDuring: { value: discard.previousDiscardTop },
    },
  ];

  if (placement) {
    flights.push({
      id: `placement-${discard.id}`,
      role,
      tone: "pickup",
      playerId: discard.playerId,
      from: handAnchor(discard),
      to: cardAnchor(discard, placement.position),
      value: placement.value,
      startsHidden: false,
      delay: 0,
      covers: [],
    });
  }

  return { id: `discard-${discard.id}`, flights, pickup: null, discard };
};

const columnGroup = (
  removal: ColumnRemovalEvent,
  previousDiscardTop: number | null,
): FlightGroup => {
  const role = roleOf(removal);
  return {
    id: `columns-${removal.id}`,
    pickup: null,
    discard: null,
    flights: removal.positions.map((position, order) => {
      const delay = COLUMN_READING_BEAT + order * 0.09;
      return {
        id: `column-${removal.id}-${position}`,
        role,
        tone: "discard",
        playerId: removal.playerId,
        from: cardAnchor(removal, position),
        to: DISCARD_PILE,
        value: removal.value,
        startsHidden: false,
        delay,
        // La colonne reste lisible le temps qu'on la voie s'aligner, puis
        // chaque case se vide à l'instant où sa carte décolle.
        covers: [
          {
            playerId: removal.playerId,
            index: position,
            face: { state: "revealed" as const, value: removal.value },
            fadesAt: delay + 0.06,
          },
        ],
        discardTopDuring: order === 0 ? { value: previousDiscardTop } : undefined,
      } satisfies CardFlight;
    }),
  };
};

const sequenceOf = (group: FlightGroup) =>
  group.pickup?.sequence ?? group.discard?.sequence ?? 0;

interface LocalMoveOptions {
  playerId: string;
  value: number;
  /** Distingue deux gestes identiques joués à deux moments du tour. */
  token: string;
}

const selfAnchor = (playerId: string): FlightAnchor => ({ kind: "hand", playerId, isSelf: true });

/**
 * Les deux gestes joués sans attendre le serveur : la carte doit partir au
 * clic, sinon elle quitterait la main avant même que son vol ne commence. Leur
 * identifiant sert aussi à ne pas rejouer l'instantané qui les confirme.
 */
export const localDiscardGroup = ({
  playerId,
  value,
  previousDiscardTop,
  token,
}: LocalMoveOptions & { previousDiscardTop: number | null }): FlightGroup => ({
  id: `local-discard-${token}-${value}`,
  pickup: null,
  discard: null,
  flights: [
    {
      id: `local-discard-${token}-${value}`,
      role: "self",
      tone: "discard",
      playerId,
      from: selfAnchor(playerId),
      to: DISCARD_PILE,
      value,
      startsHidden: false,
      delay: 0,
      covers: [],
      discardTopDuring: { value: previousDiscardTop },
    },
  ],
});

export const localReclaimGroup = ({ playerId, value, token }: LocalMoveOptions): FlightGroup => ({
  id: `local-reclaim-${token}-${value}`,
  pickup: null,
  discard: null,
  flights: [
    {
      id: `local-reclaim-${token}-${value}`,
      role: "self",
      tone: "pickup",
      playerId,
      from: { kind: "pile", pile: "discard" },
      to: selfAnchor(playerId),
      value,
      startsHidden: false,
      delay: 0,
      covers: [],
    },
  ],
});

/**
 * Traduit l'écart entre deux instantanés en mouvements de cartes. Un même
 * rendu peut porter plusieurs coups regroupés par React : ils ressortent dans
 * l'ordre où le serveur les a numérotés.
 */
export const planCardMotions = (
  previous: GameState,
  current: GameState,
  options: DeriveOptions = {},
): CardMotionPlan => {
  const pickup = derivePickup(previous, current, options);
  const discard = deriveDiscard(previous, current, options);
  const placement = derivePlacement(previous, current, discard);

  const groups: FlightGroup[] = [];
  if (pickup) groups.push(pickupGroup(pickup));
  if (discard) groups.push(discardGroup(discard, placement));
  groups.sort((first, second) => sequenceOf(first) - sequenceOf(second));

  for (const removal of deriveColumnRemovals(previous, current, options)) {
    // La carte du coup est déjà passée par la défausse : c'est elle que la pile
    // doit montrer jusqu'à ce que la colonne arrive à son tour.
    const topBeneath =
      discard !== null && discard.playerId === removal.playerId
        ? discard.value
        : previous.discardTop;
    groups.push(columnGroup(removal, topBeneath));
  }

  // Le plan est vide la plupart du temps : entre deux coups, aucun instantané
  // n'apporte de mouvement. Renvoyer la même constante plutôt qu'un littéral
  // neuf garde l'identité stable d'un rendu à l'autre — ce dont dépend toute la
  // chaîne de mémoïsation de l'écran de jeu, qui sans cela se recalcule à chaque
  // rendu et rejoue ses effets pour un résultat identique.
  if (!pickup && !discard && groups.length === 0) return EMPTY_PLAN;

  return { pickup, discard, groups };
};

/** Durée totale d'un groupe, vol différé compris. */
export const groupSpan = (group: FlightGroup): number =>
  group.flights.reduce(
    (longest, flight) => Math.max(longest, flight.delay + FLIGHT_DURATION[flight.role]),
    0,
  );

/**
 * Caches à poser sur les grilles, le premier groupe de la file étant le seul en
 * mouvement. Un vol plus ancien commande : une case ne saute pas à l'état d'un
 * coup qui n'a pas encore commencé.
 */
export const coversOf = (groups: readonly FlightGroup[]): Map<string, ActiveCover> => {
  const map = new Map<string, ActiveCover>();
  /** Groupe qui a posé chaque cache, et sa durée : de quoi borner un relais. */
  const owners = new Map<string, { index: number; span: number }>();
  groups.forEach((group, groupIndex) => {
    for (const flight of group.flights) {
      for (const cover of flight.covers) {
        const key = `${cover.playerId}:${cover.index}`;
        const held = map.get(key);
        const owner = owners.get(key);
        if (held && owner) {
          // Un groupe plus tardif reprendra cette case : le cache en place doit
          // tenir jusqu'à ce que ce groupe entre en piste. S'il s'efface avant,
          // la case se découvre sur un état que le coup a déjà dépassé — un
          // emplacement barré pour une colonne qu'on n'a pas encore vue partir —
          // puis la valeur y resurgit d'un coup, à pleine opacité.
          if (owner.index !== groupIndex) {
            map.set(key, { ...held, fadesAt: Math.max(held.fadesAt, owner.span) });
          }
          continue;
        }
        map.set(key, { ...cover, key: `${group.id}:${key}`, active: groupIndex === 0 });
        owners.set(key, { index: groupIndex, span: groupSpan(group) });
      }
    }
  });
  return map;
};

export interface DiscardTopStep {
  /** Seconde, comptée depuis maintenant, où la défausse prend cette valeur. */
  at: number;
  value: number | null;
}

/**
 * Ce que la défausse doit montrer, seconde après seconde : son sommet d'avant
 * le coup, puis la valeur de chaque carte à l'instant précis où elle s'y pose.
 * Une pile vide de suites rend la main à l'état du serveur.
 */
export const discardTimeline = (groups: readonly FlightGroup[]): DiscardTopStep[] => {
  const steps: DiscardTopStep[] = [];
  let elapsed = 0;
  for (const group of groups) {
    for (const flight of group.flights) {
      if (!landsOnDiscard(flight)) continue;
      if (steps.length === 0) {
        if (!flight.discardTopDuring) continue;
        steps.push({ at: 0, value: flight.discardTopDuring.value });
      }
      steps.push({ at: elapsed + flightLanding(flight), value: flight.value });
    }
    elapsed += groupSpan(group);
  }
  return steps;
};

/**
 * Instant où la première carte répondant au critère se posera, file d'attente
 * comprise : ce qu'elle recouvre doit se découvrir à cette seconde-là.
 */
export const arrivalOf = (
  groups: readonly FlightGroup[],
  matches: (flight: CardFlight) => boolean,
): number | null => {
  let elapsed = 0;
  for (const group of groups) {
    const flight = group.flights.find(matches);
    if (flight) return elapsed + flightLanding(flight);
    elapsed += groupSpan(group);
  }
  return null;
};

export const landsInOwnHand = (flight: CardFlight): boolean =>
  flight.to.kind === "hand" && flight.to.isSelf;
